import { filter, Observable } from 'rxjs';
import { map, retry, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RETRY_COUNT, API_RETRY_DELAY, CACHE_TIMEOUT, FIVE_MIN_CACHE_TIMEOUT } from '@shared/constants';
import { withAPICache } from '@shared/interceptors';
import { ConfigService } from '@shared/services/core/config.service';
import { InventoryRepository } from '@shared/state/inventory/inventory.repository';
import { ProductTypesRepository } from '@shared/state/inventory/product-types.repository';

@Injectable({ providedIn: 'root' })
export class ProductsDataService {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly http: HttpClient,
		private readonly repo: InventoryRepository,
		private readonly productsTypeRepo: ProductTypesRepository,
		private readonly env: ConfigService
	) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getList
	 **-------------------------------------------------------------------------------------
	 */
	public findAll(active: boolean = true): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/list?active=${+active}`;
		return this.http.get(url, {
			context: withAPICache({
				ttl: CACHE_TIMEOUT,
			}),
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getList
	 **-------------------------------------------------------------------------------------
	 */
	public getList(active: boolean = false, companyId, clearCache = false): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/list?active=${+active}`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.repo.add(resp)),
				filter((e: any) => e.company_id == companyId),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}
	/*
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 ** METHOD NAME - getProduct
	 **----------------------------------------------------------------------------------------------------------------------------------------------
	 */
	public getProduct({ companyId, productid }): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/public/product/${companyId}/${productid}`;
		return this.http.get(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypes(companyId, clearCache = false): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/product-type-list`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(
				tap(resp => this.productsTypeRepo.add(resp)),
				retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY })
			);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypeDocuments(id): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/documents/${id}`;
		return this.http.get(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public createProductTypeDocument(payload): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/documents/`;
		return this.http.post(url, { ...payload });
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public updateProductTypeDocument(payload): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/documents/`;
		return this.http.put(url, { ...payload });
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypeSefetyQuestions(id): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/safety-questions/${id}`;
		return this.http.get(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public createSafetyQuestions(id, payload): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/safety-questions/${id}`;
		return this.http.post(url, {
			...payload,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public updateEquipmentQuestions(id, payload): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/equipment-questions/${id}`;
		return this.http.put(url, {
			...payload,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypeEquipmentQuestions(id): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/equipment-questions/${id}`;
		return this.http.get(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public createEquipmentQuestions(id, payload): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/equipment-questions/${id}`;
		return this.http.post(url, {
			...payload,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypes
	 **-------------------------------------------------------------------------------------
	 */
	public updateSafetyQuestions(id, payload): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/safety-questions/${id}`;
		return this.http.put(url, {
			...payload,
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - updateProductStatus
	 **-------------------------------------------------------------------------------------
	 */
	public updateProductStatus(productId: number, status: number): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}/status`;
		return this.http.post(url, {
			status: status == 0 ? 1 : 0,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductById
	 **-------------------------------------------------------------------------------------
	 */
	public getProductById(productId: number): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}`;
		return this.http.get(url, {
			context: withAPICache({
				ttl: FIVE_MIN_CACHE_TIMEOUT,
			}),
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductById
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypeById(id: number): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/types/${id}`;
		return this.http.get(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductById
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypeVettingForm(companyId: number, productType: string): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/public/vetting-form/${encodeURIComponent(productType)}?companyId=${companyId}`;
		return this.http.get(url).pipe(
			map((e: any) => {
				return e.map((i: any) => ({
					...i,
					type: i.type === 'radio' ? 'select' : i.type,
				}));
			})
		);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - removePhoto
	 **-------------------------------------------------------------------------------------
	 */
	public removePhoto(productId: number, photoId: number): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}/remove-photo/${photoId}`;
		return this.http.delete(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveProduct
	 **-------------------------------------------------------------------------------------
	 */
	public saveProduct(form): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${form.product_id}`;
		return this.http.put(url, form);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveProduct
	 **-------------------------------------------------------------------------------------
	 */
	public createProduct(form): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/product`;
		return this.http.post(url, form);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - isProductAvailable
	 **-------------------------------------------------------------------------------------
	 */
	public isProductAvailable(productId, dateRange, reservationId: number = 0): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}/is-available`;
		return this.http.post(url, { dateRange, reservationId });
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - deleteProduct
	 **-------------------------------------------------------------------------------------
	 */
	public deleteProduct(productId: number): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}`;
		return this.http.delete(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - uploadProductPhoto
	 **-------------------------------------------------------------------------------------
	 */
	public uploadProductPhoto(productId: number, data): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}/image-upload`;
		return this.http.post(url, { data });
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - uploadProductPhoto
	 **-------------------------------------------------------------------------------------
	 */
	public uploadProductReceipt(productId: number, data): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/${productId}/receipt-upload`;
		return this.http.post(url, { data });
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveCompanyProductTypeForm
	 **-------------------------------------------------------------------------------------
	 */
	public saveCompanyProductTypeVettingForm(id, payload): Observable<any> {
		return this.http.post(`${this.env.get('productsEndPoint')}/vetting-form/${id}`, {
			...payload,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypePhotos
	 **-------------------------------------------------------------------------------------
	 */
	public getProductTypePhotos({ productTypeId }): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/product-type-photos/?productTypeId=${encodeURIComponent(productTypeId)}`;
		return this.http.get(url, {
			context: withAPICache({
				key: ``,
			}),
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypePhotos
	 **-------------------------------------------------------------------------------------
	 */
	public createProductTypePhotos({ productTypeId, ownerId, ownerType, name, category, required, type }): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/product-type-photos`;
		return this.http.post(url, {
			productTypeId,
			ownerId,
			ownerType,
			name,
			category,
			required,
			type,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypePhotos
	 **-------------------------------------------------------------------------------------
	 */
	public deleteProductTypePhotos({ id }): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/product-type-photos/${id}`;
		return this.http.delete(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductTypePhotos
	 **-------------------------------------------------------------------------------------
	 */
	public updateProductTypePhotos({ id, productTypeId, ownerId, ownerType, name, category, required, type }): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/product-type-photos/${id}`;
		return this.http.put(url, {
			productTypeId,
			ownerId,
			ownerType,
			name,
			category,
			required,
			type,
		});
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductById
	 **-------------------------------------------------------------------------------------
	 */
	public getCompanyDocumentsByType(companyId: number, productType: string): Observable<any> {
		const url = `${this.env.get('productsEndPoint')}/public/documents/${encodeURIComponent(productType)}?companyId=${companyId}`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
					key: `${companyId}-${encodeURIComponent(productType)}-companyRequiredDocs`,
				}),
				headers: {
					'x-api-key': this.env.get('apiKey'),
				},
			})
			.pipe(
				map((item: any) => {
					return {
						waiver: {
							name: 'Waiver',
							doc: item.find(i => i.document_type === 'waiver')?.document || 'No Document Supplied',
						},
						rentalAgreement: {
							name: 'Rental Agreement',
							doc: item.find(i => i.document_type === 'rental_agreement')?.document || 'No Document Supplied',
						},
						navRules: {
							name: 'Navigation Rules',
							doc: item.find(i => i.document_type === 'navigation_rules')?.document || 'No Document Supplied',
						},
						acknowledgement: {
							name: 'Acknowledgement',
							doc: item.find(i => i.document_type === 'acknowledgement')?.document || 'No Document Supplied',
						},
					};
				})
			);
	}
}
